import React, { useState } from 'react';
import { ScriptCandidate } from '@kaizen/shared';
import { Terminal, Play, Eye, Copy, Check, FileCode } from 'lucide-react';
import { formatBytes, formatRelativeTime } from '../../lib/utils';
import { StatusBadge } from './StatusBadge';

interface ScriptCardProps {
  script: ScriptCandidate;
  onRun: (script: ScriptCandidate) => void;
  onView: (script: ScriptCandidate) => void;
}

export const ScriptCard: React.FC<ScriptCardProps> = ({ script, onRun, onView }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(script.command || script.scriptContent || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="acrylic-card p-4 hover:border-slate-300 transition-all">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
            <FileCode className="w-4 h-4" />
          </div>
          <div>
            <h4 className="font-mono text-sm font-semibold text-slate-800 truncate max-w-full sm:max-w-[240px]">
              {script.filename}
            </h4>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{script.type}</span>
              <span>•</span>
              <span>{formatBytes(script.size)}</span>
              <span>•</span>
              <span>{formatRelativeTime(script.receivedTime)}</span>
            </div>
          </div>
        </div>
        <StatusBadge status={script.status} />
      </div>

      <div className="bg-slate-900 rounded-lg p-2.5 my-3 text-slate-200 font-mono text-xs overflow-x-auto max-h-24">
        <code>{script.command || script.scriptContent}</code>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="text-[11px] text-slate-400">
          Source: {script.source}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleCopy}
            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors"
            title="Copy command"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => onView(script)}
            className="flex items-center gap-1 px-2.5 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200 transition-colors"
          >
            <Eye className="w-3 h-3" />
            <span>Inspect</span>
          </button>
          <button
            onClick={() => onRun(script)}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm transition-colors"
          >
            <Play className="w-3 h-3 fill-current" />
            <span>Run</span>
          </button>
        </div>
      </div>
    </div>
  );
};
